import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';

import {
  AppState,
  AuthorizationParams,
  AuthService,
  LogoutOptions,
  RedirectLoginOptions,
} from '@auth0/auth0-angular';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Store } from '@ngrx/store';
import { combineLatest, filter, Subscription, tap } from 'rxjs';

import { environment } from '@/environments/environment';
import { LoggedInUserActions } from '@actions/logged-in-user.actions';
import { FirsTechUser } from '@models';
import {
  selectHasWarning,
  selectIsUserTimedout,
} from '@selectors/logged-in-user.selector';

@Injectable({
  providedIn: 'root',
})
export class UserService implements OnDestroy {
  private httpClient: HttpClient = inject(HttpClient);
  private auth: AuthService = inject(AuthService);
  private idle: Idle = inject(Idle);
  private store: Store = inject(Store);

  private subscriptions: Subscription[] = [];

  constructor() {
    this.subscriptions.push(
      this.store
        .select(selectIsUserTimedout)
        .pipe(filter((isTimedOut) => !!isTimedOut))
        .subscribe(() => {
          this.logout({
            logoutParams: {
              returnTo: new URL(
                '/session-timeout',
                environment.applicationRoot,
              ).toString(),
            },
          });
        }),
    );
  }

  public loadCurrentUser() {
    return this.httpClient.get<FirsTechUser>(
      environment.serviceEndpoints.users.toString(),
    );
  }

  public loadUser(userId: number) {
    return this.httpClient.get<FirsTechUser>(
      new URL(userId.toString(), environment.serviceEndpoints.users).toString(),
    );
  }

  public login(targetPage?: string): void;
  public login(options?: RedirectLoginOptions<AppState>): void;
  public login(options?: string | RedirectLoginOptions<AppState>): void {
    if (!!options && typeof options === 'string') {
      this.auth.loginWithRedirect({
        ...environment.auth0,
        authorizationParams: {
          ...(environment.auth0.authorizationParams as Omit<
            AuthorizationParams,
            'scope'
          >),
        },
        appState: {
          target: options,
        },
      });
    } else if (!!options && typeof options === 'object') {
      this.auth.loginWithRedirect({
        ...environment.auth0,
        authorizationParams: {
          ...(environment.auth0.authorizationParams as Omit<
            AuthorizationParams,
            'scope'
          >),
        },
        ...options,
      });
    } else {
      this.auth.loginWithRedirect({
        ...environment.auth0,
        authorizationParams: {
          ...(environment.auth0.authorizationParams as Omit<
            AuthorizationParams,
            'scope'
          >),
        },
        appState: {
          target: '/home/dashboard',
        },
      });
    }
  }

  /**
   * This should only be invoked by the logout effect in app-user.effects,
   * which will handle clearing the session storage and dispatching the
   * appropriate actions. This is because we want to ensure that all of
   * those things happen together, and that we don't accidentally forget
   * to clear the session storage or dispatch an action if we were to
   * call this method directly from a component.
   * @param options Logout options for Auth0.
   */
  public logout(options?: LogoutOptions) {
    this.auth.logout(options);
  }

  public initializeIdleTimeoutHandling() {
    // Set up idle/timeout
    this.idle.setIdle(environment.userIdleTimeoutConfig.idleLength); // how long can they be inactive before considered idle, in seconds
    this.idle.setTimeout(environment.userIdleTimeoutConfig.timeoutLength); // how long can they be idle before considered timed out, in seconds
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // provide sources that will "interrupt" aka provide events indicating the user is active

    this.subscriptions.push(
      this.idle.onIdleEnd.subscribe((evnt) => {
        console.log('this caused the idle to not go through', evnt);
        this.store.dispatch(LoggedInUserActions.isNotIdle()); // eslint-disable-line @ngrx/avoid-dispatching-multiple-actions-sequentially
        this.store.dispatch(LoggedInUserActions.acknowledgedTimeoutWarning()); // eslint-disable-line @ngrx/avoid-dispatching-multiple-actions-sequentially
      }),
    );

    this.subscriptions.push(
      this.idle.onIdleStart.subscribe(() => {
        this.store.dispatch(LoggedInUserActions.isIdle());
      }),
    );
    this.subscriptions.push(
      this.idle.onTimeout.subscribe(() => {
        this.store.dispatch(LoggedInUserActions.isTimedout());
      }),
    );
    this.subscriptions.push(
      combineLatest([
        this.idle.onTimeoutWarning,
        this.store.select(selectHasWarning),
      ])
        .pipe(
          tap(([timeTillTimeout, hasWarning]) => {
            console.log(
              'time till logout and has been warned:',
              timeTillTimeout,
              hasWarning,
            );
          }),
          filter(
            ([timeTillTimeout, hasWarning]) =>
              timeTillTimeout <=
                environment.userIdleTimeoutConfig.showWarningFor && !hasWarning,
          ),
        )
        .subscribe(() => {
          this.store.dispatch(LoggedInUserActions.hasTimeoutWarning());
        }),
    );
  }

  public startWatchingForIdleTimeout() {
    console.info('Starting idle watching...');
    this.idle.watch();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
